import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IPurchaseActivityItem,
  IPurchaseActivityList,
  IPurchaseActivityResponse,
} from '../data/purchase-activity.models';
import { PurchaseActivityHttpService } from '../services/purchase-activity.http.service';
import * as PurchaseActivitySelectors from '../data/state/purchase-activity.reducer';
import {
  EPurchaseActivityEndpoints,
  EPurchaseActivityPreviousOrdersTabs,
  EPurchaseActivityTabs,
} from '../data/purchase-activity.configurations';
import { Store } from '@ngrx/store';
import { State } from '../../../reducers';
import { PurchaseActivityActions } from '../../../actions';

@Injectable({providedIn: 'root'})
export class PurchaseActivityFacade {
  private purchaseActivityHttpService: PurchaseActivityHttpService = inject(PurchaseActivityHttpService);
  private store: Store<State> = inject(Store<State>);

  // *** Selectors ***

  selectPreviousOrders(): Observable<IPurchaseActivityList> {
    return this.store.select(PurchaseActivitySelectors.selectPreviousOrders);
  }

  selectArchiveOrders(): Observable<IPurchaseActivityList> {
    return this.store.select(PurchaseActivitySelectors.selectArchiveOrders);
  }

  selectPendingOrders(): Observable<IPurchaseActivityList> {
    return this.store.select(PurchaseActivitySelectors.selectPendingOrders);
  }

  selectPreviousQuotes(): Observable<IPurchaseActivityList> {
    return this.store.select(PurchaseActivitySelectors.selectPreviousQuotes);
  }

  selectPreviousRequests(): Observable<IPurchaseActivityList> {
    return this.store.select(PurchaseActivitySelectors.selectPreviousRequests);
  }

  selectPurchaseActivityDetails(): Observable<IPurchaseActivityItem | null> {
    return this.store.select(PurchaseActivitySelectors.selectPurchaseActivityDetails);
  }

  selectPurchaseActivityDetailsLoading(): Observable<boolean> {
    return this.store.select(PurchaseActivitySelectors.selectIsPurchaseActivityDetailsLoading);
  }

  // *** Executing HTTP Requests ***

  getPurchaseActivityData(endpoint: EPurchaseActivityEndpoints, page: number): Observable<IPurchaseActivityResponse> {
    return this.purchaseActivityHttpService.getPurchaseActivityData(endpoint, page);
  }

  getPurchaseActivityItem(endpoint: EPurchaseActivityEndpoints, itemId: string): Observable<IPurchaseActivityResponse> {
    return this.purchaseActivityHttpService.getPurchaseActivityItem(endpoint, itemId);
  }

  // *** Dispatching Actions ***

  loadPurchaseActivityData(tab: string, page: number): void {
    this.store.dispatch(PurchaseActivityActions.setIsLoadedForTab({tab, isLoaded: false}));
    switch (tab) {
      case EPurchaseActivityPreviousOrdersTabs.ORDERS:
        this.store.dispatch(PurchaseActivityActions.loadOrdersInPreviousOrders(page));
        break;
      case EPurchaseActivityPreviousOrdersTabs.ARCHIVE:
        this.store.dispatch(PurchaseActivityActions.loadArchiveInPreviousOrders(page));
        break;
      case EPurchaseActivityTabs.PENDING_ORDERS:
        this.store.dispatch(PurchaseActivityActions.loadOrdersInPendingOrders(page));
        break;
      case EPurchaseActivityTabs.PREVIOUS_QUOTES:
        this.store.dispatch(PurchaseActivityActions.loadQuotesInPreviousQuotes(page));
        break;
      case EPurchaseActivityTabs.PREVIOUS_REQUESTS:
        this.store.dispatch(PurchaseActivityActions.loadRequestsInPreviousRequests(page));
        break;
    }
  }

  setPurchaseActivityDetails(data: IPurchaseActivityItem): void {
    this.store.dispatch(PurchaseActivityActions.setPurchaseActivityDetails(data));
  }

  loadPurchaseActivityDetails(id: string, endpointType: string): void {
    this.store.dispatch(PurchaseActivityActions.loadPurchaseActivityDetails(id, endpointType));
  }

  changePagination(tab: string, page: number): void {
    this.store.dispatch(PurchaseActivityActions.changePagination({tab, page}));
  }

  deleteItemFromPendingOrder(tab: string, page: number, orderId: string, orderItemId: string): void {
    this.store.dispatch(PurchaseActivityActions.deleteItemFromPendingOrder({orderId, orderItemId}));
    this.store.dispatch(PurchaseActivityActions.setIsLoadedForItem({tab, page, orderId, isLoaded: false}));
  }

  clearPurchaseActivitySection(tab: string): void {
    this.store.dispatch(PurchaseActivityActions.clearPurchaseActivitySection({tab}));
  }

  updatePreviousRequestVersionStatus(tab: string, page: number, orderId: string, newStatus: string): void {
    this.store.dispatch(PurchaseActivityActions.updatePreviousRequestVersionStatus({tab, page, orderId, newStatus}));
  }
}
